import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Pencil,
  Eye,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Stethoscope,
  FileText,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Calendar,
  CheckCircle,
} from "lucide-react";
import type { Constante } from "../../types";
import { getAuthHeaders } from "../../services/api/apiConfig";
import { useConfirm } from "../../context/ConfirmContext";


interface ConstanteTableProps {
  role: "admin" | "doctor" | "secretary";
  constantes: Constante[];
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  filterStatus?: string | null;
  setFilterStatus?: React.Dispatch<React.SetStateAction<string | null>>;
  showConsulted?: boolean;
  setShowConsulted: React.Dispatch<React.SetStateAction<boolean>>;
  showTodayOnly?: boolean;
  setShowTodayOnly: React.Dispatch<React.SetStateAction<boolean>>;
  currentPage?: number;
  setCurrentPage?: React.Dispatch<React.SetStateAction<number>>;
  constantesPerPage?: number;
  sortOrder?: "asc" | "desc" | null;
  setSortOrder?: React.Dispatch<React.SetStateAction<"asc" | "desc" | null>>;
  pagination?: {
    page: number;
    totalPages: number;
    totalItems: number;
  };
  onMarkAsConsulted?: (constante: Constante) => Promise<void>;
  processingConsultation?: number | null;
}

const ConstanteTable: React.FC<ConstanteTableProps> = ({
  role,
  constantes,
  searchTerm,
  setSearchTerm,
  showConsulted = true,
  setShowConsulted,
  showTodayOnly = false,
  setShowTodayOnly,
  currentPage = 1,
  setCurrentPage,
  constantesPerPage = 10,
  sortOrder = "desc",
  setSortOrder,
  pagination,
  onMarkAsConsulted,
  processingConsultation,
}) => {
  const navigate = useNavigate();
  const { confirm } = useConfirm();

  const displayedConstantes = React.useMemo(
    () => constantes ?? [],
    [constantes]
  );

  const handleViewConstante = (constante: Constante) => {
    navigate(`/${role}/dashboard/constante/view/${constante.id}`, {
      state: { constante },
    });
  };

  const handleUpdateConstante = (constanteId: number) => {
    navigate(`/${role}/dashboard/constante/edit/${constanteId}`);
  };

  const handleStartConsultation = (constante: Constante) => {
    const patientId = constante.patient?.id || constante.patientId;
    if (!patientId) {
      console.error("Patient ID non trouvé pour la constante:", constante.id);
      return;
    }

    // Utiliser la même route pour admin et doctor
    navigate(`/${role}/dashboard/consultation/new/${patientId}`, {
      state: {
        constanteId: constante.id,
        patientName: constante.patientFullName ||
          (constante.patient
            ? `${constante.patient.firstName || ""} ${constante.patient.lastName || ""}`.trim()
            : `${constante.firstName || ""} ${constante.lastName || ""}`.trim()),
        constante,
      },
    });
  };

  const handleViewMedicalRecord = async (constante: Constante) => {
    const patientId = constante.patient?.id || constante.patientId;
    if (!patientId) {
      console.error("Patient ID non trouvé pour la constante:", constante.id);
      return;
    }

    try {
      // 1. Vérifier s'il existe des dossiers médicaux pour ce patient
      const response = await fetch(`/api/medical-records/by-patient/${patientId}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const medicalRecords = await response.json();

      if (medicalRecords.length > 0) {
        // 2A. Dossiers existants : utiliser le plus récent
        const latestRecord = medicalRecords.sort((a: any, b: any) =>
          new Date(b.dateCreation).getTime() - new Date(a.dateCreation).getTime()
        )[0];

        navigate(`/${role}/dashboard/record/view/${latestRecord.id}`, {
          state: {
            constanteId: constante.id,
            patientName: constante.patientFullName,
            source: "existing_record"
          },
        });
      } else {
        // 2B. Aucun dossier existant : rediriger vers la création
        navigate(`/${role}/dashboard/record/new`, {
          state: {
            patientId: patientId,
            constanteId: constante.id,
            patientName: constante.patientFullName,
            source: "no_existing_record",
            prefillData: {
              patientId: patientId,
              doctorId: constante.doctorId,
              constanteId: constante.id
            }
          },
        });
      }
    } catch (error) {
      console.error("Erreur lors de la vérification des dossiers médicaux:", error);

      // 3. Fallback sécurisé en cas d'erreur
      const isConfirmed = await confirm(
        "Impossible de vérifier les dossiers existants. Voulez-vous créer un nouveau dossier médical pour ce patient ?",
        {
          title: "Erreur de vérification",
          confirmText: "Créer un dossier",
          cancelText: "Annuler"
        }
      );

      if (isConfirmed) {
        navigate(`/${role}/dashboard/record/new`, {
          state: {
            patientId: patientId,
            constanteId: constante.id,
            patientName: constante.patientFullName,
            source: "error_fallback"
          },
        });
      }
      // Si l'utilisateur annule, ne rien faire
    }
  };
  const handleMarkAsConsulted = async (constante: Constante) => {
    if (onMarkAsConsulted) {
      try {
        await onMarkAsConsulted(constante);
      } catch (error) {
        console.error("Erreur lors de la consultation:", error);
      }
    }
  };

  const handleSortChange = () => {
    if (!setSortOrder) return;
    if (sortOrder === "desc") {
      setSortOrder("asc");
    } else if (sortOrder === "asc") {
      setSortOrder(null);
    } else {
      setSortOrder("desc");
    }
  };

  const getDisplayValue = (constante: Constante, key: string): string => {
    const value = constante[key as keyof Constante];

    if (value === null || value === undefined) {
      return "N/A";
    }

    if (typeof value === "object") {
      return "N/A";
    }

    return String(value);
  };

  const totalPagesFromServer = pagination?.totalPages ?? 0;
  const totalItems = pagination?.totalItems ?? displayedConstantes.length;
  const effectiveTotalPages = Math.max(totalPagesFromServer, 1);
  const currentConstantes = displayedConstantes;
  const pageStart =
    totalItems === 0 ? 0 : (currentPage - 1) * constantesPerPage + 1;
  const pageEnd =
    totalItems === 0
      ? 0
      : Math.min(currentPage * constantesPerPage, totalItems);

  const paginate = (page: number) => {
    if (!setCurrentPage) return;
    const next = Math.min(Math.max(page, 1), effectiveTotalPages);
    setCurrentPage(next);
  };
  const nextPage = () =>
    setCurrentPage &&
    setCurrentPage((prev) => Math.min(prev + 1, effectiveTotalPages));
  const prevPage = () =>
    setCurrentPage && setCurrentPage((prev) => Math.max(prev - 1, 1));
  const firstPage = () => paginate(1);
  const lastPage = () => paginate(effectiveTotalPages);

  const columns = [
    { label: "Patient", key: "patientFullName", class: "md:w-1/7" },
    { label: "Médecin", key: "doctorName", class: "md:w-1/7" },
    {
      label: "Téléphone",
      key: "patientPhone",
      class: "hidden lg:table-cell md:w-1/8",
    },
    ...(role === "admin"
      ? [
        {
          label: "Poids",
          key: "weight",
          class: "hidden lg:table-cell md:w-1/8",
        },
        {
          label: "Taille",
          key: "height",
          class: "hidden lg:table-cell md:w-1/8",
        },
        { label: "Tension", key: "bloodPressure", class: "md:w-1/8" },
        { label: "Rythme cardiaque", key: "heartRate", class: "md:w-1/8" },
        { label: "Température", key: "temperature", class: "md:w-1/8" },
      ]
      : []),
    { label: "Motif", key: "reason", class: "md:w-1/7" },
    ...(role !== "doctor"
      ? []
      : []),
    { label: "Actions", key: "actions", class: "md:w-1/6" },
  ];

  const canPerformAction = (action: string, constante: Constante) => {
    switch (action) {
      case "consult": {
        const roleAllowed = role === "doctor" || role === "admin";
        const statusAllowed =
          constante.status === "APPROVED" ||
          constante.status === "PENDING" ||
          !constante.status;
        const notYetConsulted = !constante.hasConsultation;

        const canConsult = roleAllowed && statusAllowed && notYetConsulted;

        return canConsult;
      }
      case "mark_consulted": {
        const roleAllowed = role === "doctor" || role === "admin";
        const statusAllowed =
          constante.status === "APPROVED" ||
          constante.status === "PENDING" ||
          !constante.status;
        const notYetConsulted = !constante.hasConsultation;

        return roleAllowed && statusAllowed && notYetConsulted;
      }
      case "medical_record": {
        const canViewRecord =
          role === "doctor" || role === "admin" || role === "secretary";
        return canViewRecord;
      }
      default:
        return true;
    }
  };

  return (
    <div
      className={`bg-white rounded-2xl p-6 shadow-lg border border-gray-100 ${role === "admin" ? "" : "max-w-7xl mx-auto"
        }`}
    >
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-grow">
          <Search
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            size={18}
          />
          <input
            type="text"
            placeholder="Rechercher par patient, médecin ou téléphone..."
            className={`w-full pl-10 pr-4 py-2 border ${role === "admin"
              ? "border-gray-200 bg-gray-50"
              : "border-gray-300"
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-2">
          <select
            className={`px-4 py-2 border ${role === "admin"
              ? "border-gray-200 bg-gray-50"
              : "border-gray-300"
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
            value={showConsulted ? "all" : "not_consulted"}
            onChange={(e) => setShowConsulted(e.target.value === "all")}
          >
            <option value="not_consulted">Non consultées</option>
            <option value="all">Toutes les constantes</option>
          </select>

          <button
            onClick={() => setShowTodayOnly(!showTodayOnly)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${showTodayOnly
              ? "bg-blue-500 text-white border-blue-500"
              : role === "admin"
                ? "border-gray-200 bg-gray-50 hover:bg-gray-100"
                : "border-gray-300 hover:bg-gray-50"
              } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
            title={
              showTodayOnly
                ? "Afficher toutes les constantes"
                : "Afficher uniquement les constantes du jour"
            }
          >
            <Calendar size={16} />
            <span className="hidden sm:inline">
              {showTodayOnly ? "Aujourd'hui" : "Toutes dates"}
            </span>
          </button>

          {setSortOrder && (
            <button
              onClick={handleSortChange}
              className={`flex items-center gap-2 px-4 py-2 border ${role === "admin"
                ? "border-gray-200 bg-gray-50 hover:bg-gray-100"
                : "border-gray-300 hover:bg-gray-50"
                } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors`}
              title={
                sortOrder === "desc"
                  ? "Trié du plus récent au plus ancien"
                  : sortOrder === "asc"
                    ? "Trié du plus ancien au plus récent"
                    : "Tri par défaut"
              }
            >
              {sortOrder === "desc" ? (
                <>
                  <ArrowDown size={16} />
                  <span className="hidden sm:inline">Récent → Ancien</span>
                </>
              ) : sortOrder === "asc" ? (
                <>
                  <ArrowUp size={16} />
                  <span className="hidden sm:inline">Ancien → Récent</span>
                </>
              ) : (
                <>
                  <ArrowUpDown size={16} />
                  <span className="hidden sm:inline">Trier par date</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      <div className="mb-4 text-sm text-gray-600">
        {showTodayOnly && (
          <div className="flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-2 rounded-lg">
            <Calendar size={14} />
            <span>
              {totalItems} constante(s) du jour
              {!showConsulted && " non consultée(s)"}
            </span>
          </div>
        )}
      </div>

      <div className="overflow-x-auto -mx-2 sm:-mx-4 px-2 sm:px-4">
        <table className="w-full table-auto divide-y divide-gray-200 min-w-[600px]">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${col.class}`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {currentConstantes.length > 0 ? (
              currentConstantes.map((constante, index) => {
                const isConsulted = constante.hasConsultation;
                const rowClasses = `hover:bg-gray-50 transition-colors ${isConsulted
                  ? "bg-green-50 border-l-4 border-l-green-400 opacity-75"
                  : "bg-blue-50 border-l-4 border-l-blue-400"
                  }`;

                return (
                  <tr
                    key={constante.id}
                    className={rowClasses}
                    style={
                      role === "admin"
                        ? {
                          animation: `slideInUp 0.6s ease-out forwards`,
                          animationDelay: `${index * 100}ms`,
                        }
                        : {}
                    }
                  >
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={`px-4 py-3 whitespace-nowrap text-sm ${col.key === "patientFullName"
                          ? "font-medium text-gray-900"
                          : "text-gray-500"
                          } ${col.class || ""}`}
                      >
                        {col.key === "actions" ? (
                          <div className="flex justify-center space-x-1">
                            <button
                              className={`p-1.5 rounded-full ${role === "admin"
                                ? "bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-800"
                                : "text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                } transition-colors`}
                              onClick={() => handleViewConstante(constante)}
                              title="Voir les détails"
                            >
                              <Eye size={16} />
                            </button>
                            {role !== "doctor" && (
                              <button
                                className={`p-1.5 rounded-full ${role === "admin"
                                  ? "bg-yellow-50 text-yellow-600 hover:bg-yellow-100 hover:text-yellow-700"
                                  : "text-yellow-500 hover:text-yellow-700 hover:bg-yellow-50"
                                  } transition-colors`}
                                onClick={() =>
                                  handleUpdateConstante(constante.id)
                                }
                                title="Modifier"
                              >
                                <Pencil size={16} />
                              </button>
                            )}
                            {canPerformAction("consult", constante) && (
                              <button
                                className={`p-1.5 rounded-full ${role === "admin"
                                  ? "bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-700"
                                  : "text-green-600 hover:text-green-700 hover:bg-green-50"
                                  } transition-colors`}
                                onClick={() =>
                                  handleStartConsultation(constante)
                                }
                                title="Commencer une consultation"
                              >
                                <Stethoscope size={16} />
                              </button>
                            )}
                            {canPerformAction("mark_consulted", constante) &&
                              onMarkAsConsulted && (
                                <button
                                  className={`p-1.5 rounded-full ${role === "admin"
                                    ? "bg-orange-50 text-orange-600 hover:bg-orange-100 hover:text-orange-700"
                                    : "text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                    } transition-colors ${processingConsultation === constante.id
                                      ? "opacity-50 cursor-not-allowed"
                                      : ""
                                    }`}
                                  onClick={() =>
                                    handleMarkAsConsulted(constante)
                                  }
                                  title="Marquer comme consulté"
                                  disabled={
                                    processingConsultation === constante.id
                                  }
                                >
                                  <CheckCircle size={16} />
                                </button>
                              )}
                            {canPerformAction("medical_record", constante) && (
                              <button
                                className={`p-1.5 rounded-full ${role === "admin"
                                  ? "bg-purple-50 text-purple-600 hover:bg-purple-100 hover:text-purple-700"
                                  : "text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                  } transition-colors`}
                                onClick={() =>
                                  handleViewMedicalRecord(constante)
                                }
                                title="Voir le dossier médical"
                              >
                                <FileText size={16} />
                              </button>
                            )}
                          </div>
                        ) : col.key === "patientFullName" ? (
                          constante.patientFullName ||
                          (constante.patient
                            ? `${constante.patient.firstName || ""} ${constante.patient.lastName || ""
                              }`.trim()
                            : `${constante.firstName || ""} ${constante.lastName || ""
                              }`.trim()) ||
                          "N/A"
                        ) : col.key === "doctorName" ? (
                          constante.doctorName ||
                          (constante.doctor
                            ? `Dr. ${constante.doctor.firstName || ""} ${constante.doctor.lastName || ""
                              }`.trim()
                            : "N/A")
                        ) : col.key === "patientPhone" ? (
                          constante.patientPhone ||
                          constante.patient?.phone ||
                          "N/A"
                        ) : col.key === "status" ? (
                          constante.status || "N/A"
                        ) : (
                          getDisplayValue(constante, col.key)
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-8 text-center text-gray-500"
                >
                  <div className="flex flex-col items-center gap-2">
                    {showTodayOnly ? (
                      <>
                        <Calendar size={24} className="text-gray-400" />
                        <span>Aucune constante trouvée pour aujourd'hui</span>
                      </>
                    ) : (
                      <span>Aucune constante trouvée</span>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {role === "doctor" && (
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
          <div className="flex items-center space-x-4 text-xs text-gray-500">
            <div className="flex items-center space-x-1">
              <Stethoscope size={14} />
              <span>Consultation</span>
            </div>
            <div className="flex items-center space-x-1">
              <CheckCircle size={14} />
              <span>Marquer consulté</span>
            </div>
            <div className="flex items-center space-x-1">
              <FileText size={14} />
              <span>Dossier médical</span>
            </div>
          </div>
          {showTodayOnly && (
            <div className="text-xs text-blue-600">
              <Calendar size={14} className="inline mr-1" />
              Vue: Constantes du jour
            </div>
          )}
        </div>
      )}

      {(role === "admin" || role === "doctor") && setCurrentPage && (
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center mt-4">
          <div className="text-sm text-gray-600 mb-2 md:mb-0">
            {totalItems === 0
              ? "Aucune constante à afficher"
              : `Affichage des constantes ${pageStart} à ${pageEnd} sur ${totalItems}`}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={firstPage}
              disabled={currentPage === 1}
              className={`p-1.5 rounded-md ${currentPage === 1
                ? "text-gray-400 cursor-not-allowed"
                : "text-gray-700 hover:bg-gray-100"
                }`}
            >
              <ChevronsLeft size={16} />
            </button>
            <button
              onClick={prevPage}
              disabled={currentPage === 1}
              className={`p-1.5 rounded-md ${currentPage === 1
                ? "text-gray-400 cursor-not-allowed"
                : "text-gray-700 hover:bg-gray-100"
                }`}
            >
              <ChevronLeft size={16} />
            </button>
            {Array.from(
              { length: Math.min(5, effectiveTotalPages) },
              (_, i) => {
                let pageNum;
                if (effectiveTotalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= effectiveTotalPages - 2) {
                  pageNum = effectiveTotalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => paginate(pageNum)}
                    className={`w-8 h-8 rounded-md text-sm ${currentPage === pageNum
                      ? "bg-blue-600 text-white"
                      : "text-gray-700 hover:bg-gray-100"
                      }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            <button
              onClick={nextPage}
              disabled={currentPage === effectiveTotalPages}
              className={`p-1.5 rounded-md ${currentPage === effectiveTotalPages
                ? "text-gray-400 cursor-not-allowed"
                : "text-gray-700 hover:bg-gray-100"
                }`}
            >
              <ChevronRight size={16} />
            </button>
            <button
              onClick={lastPage}
              disabled={currentPage === effectiveTotalPages}
              className={`p-1.5 rounded-md ${currentPage === effectiveTotalPages
                ? "text-gray-400 cursor-not-allowed"
                : "text-gray-700 hover:bg-gray-100"
                }`}
            >
              <ChevronsRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConstanteTable;